
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  MessageSquare, Send, ArrowLeft, Trash2, 
  Sparkles, ExternalLink, History, RefreshCw,
  FileSearch, Library, BookOpen, Search, ChevronDown, ChevronUp,
  Zap, GraduationCap, Sun, Moon
} from 'lucide-react';
import { ChatSession, GeminiContent, ViewState, ChatSummary } from '../types';
import { 
  subscribeToChatSessions, createChatSession, updateChatHistory, deleteChatSession, getChatHistory, 
  subscribeToChatSummaries, saveChatSummary, deleteChatSummary, recordROIEvent
} from '../services/db';
import { ROIEventType } from '../types';
import { sendChatMessageStream, generateSessionSummary, logUsage } from '../services/geminiService';
import { sanitizeHtml } from '../utils/sanitize';
import { User } from 'firebase/auth';

interface ChatScreenProps {
  user: User | null;
  onNavigate?: (view: ViewState) => void;
}

const ChatScreen: React.FC<ChatScreenProps> = ({ user, onNavigate }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('module_theme_chat');
    if (saved) return saved === 'dark';
    const theme = localStorage.getItem('app_theme') || 'mixed';
    return theme === 'dark';
  });

  useEffect(() => {
    const handleThemeChange = () => {
      const saved = localStorage.getItem('module_theme_chat');
      if (!saved) {
        const theme = localStorage.getItem('app_theme') || 'mixed';
        setIsDarkMode(theme === 'dark');
      }
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('module_theme_chat', next ? 'dark' : 'day');
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  const [activeTab, setActiveTab] = useState<'CHAT' | 'SUMMARIES'>('CHAT');
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [summaries, setSummaries] = useState<ChatSummary[]>([]);
  const [currentHistory, setCurrentHistory] = useState<GeminiContent[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [summarizingId, setSummarizingId] = useState<string | null>(null);
  
  const [summarySearch, setSummarySearch] = useState('');
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());

  const [viewingProMap, setViewingProMap] = useState<Record<number, boolean>>({});
  const [loadingProMap, setLoadingProMap] = useState<Record<number, boolean>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const parseIAContent = (raw: string) => {
    if (!raw) return "";
    let clean = raw
      .replace(/```(html|markdown|text)?/gi, "")
      .replace(/```/g, "")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/(\*\*|__)(.*?)\1/g, "<b>$2</b>")
      .replace(/\*/g, "")
      .replace(/\n\s*\n+/g, "\n\n") // Normalizar a doble salto físico
      .replace(/\n/g, "<br>");      // Convertir a HTML
    
    return clean.trim();
  };

  useEffect(() => {
    if (!user) return;
    const unsubSessions = subscribeToChatSessions(user.uid, setSessions);
    const unsubSummaries = subscribeToChatSummaries(user.uid, setSummaries);
    return () => { unsubSessions(); unsubSummaries(); };
  }, [user]);

  useEffect(() => {
    if (!user || !activeSession) { setCurrentHistory([]); return; }
    if (activeSession.id !== 'new') { 
        getChatHistory(user.uid, activeSession.id).then(history => {
            setCurrentHistory(history);
            const newMap: Record<number, boolean> = {};
            history.forEach((msg, idx) => {
                if (msg.role === 'model' && msg.proParts?.[0]?.text) {
                    newMap[idx] = true;
                }
            });
            setViewingProMap(newMap);
        }); 
    }
  }, [activeSession, user]);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 150;
  };

  useEffect(() => { 
    if (isAtBottomRef.current && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' }); 
    }
  }, [currentHistory, isSending]);

  const filteredSummaries = useMemo(() => {
    const term = summarySearch.toLowerCase();
    return summaries.filter(s => 
      s.title.toLowerCase().includes(term) || 
      s.content.toLowerCase().includes(term)
    );
  }, [summaries, summarySearch]);

  const toggleSummaryExpansion = (id: string) => {
    setExpandedSummaries(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !user || isSending) return;
    const textToSend = inputMessage;
    setInputMessage('');
    setIsSending(true);
    try {
      let sessionId = activeSession?.id;
      if (sessionId === 'new' || !sessionId) {
        sessionId = await createChatSession(user.uid, textToSend);
        setActiveSession({ id: sessionId, userId: user.uid, title: textToSend, historial: [], createdAt: Date.now(), updatedAt: Date.now() });
      }
      const newUserMsg: GeminiContent = { role: 'user', parts: [{ text: textToSend }] };
      const modelPlaceholder: GeminiContent = { role: 'model', parts: [{ text: "" }] };
      
      const updatedInitialHistory = [...currentHistory, newUserMsg, modelPlaceholder];
      setCurrentHistory(updatedInitialHistory);
      await updateChatHistory(user.uid, sessionId!, updatedInitialHistory);
      
      await runEngine(textToSend, sessionId!, 'flash', updatedInitialHistory.length - 1);
    } catch (e: any) { console.error(`Error: ${e.message}`); } finally { setIsSending(false); }
  };

  const handleRequestProAnalysis = async (idx: number) => {
    if (!user || !activeSession || loadingProMap[idx]) return;
    
    const userPromptMsg = currentHistory[idx - 1];
    if (!userPromptMsg || userPromptMsg.role !== 'user') return;

    setLoadingProMap(prev => ({ ...prev, [idx]: true }));
    setViewingProMap(prev => ({ ...prev, [idx]: true }));

    try {
        await runEngine(userPromptMsg.parts[0].text, activeSession.id, 'pro', idx);
    } finally {
        setLoadingProMap(prev => ({ ...prev, [idx]: false }));
    }
  };

  const runEngine = async (msg: string, sId: string, type: 'flash' | 'pro', targetIdx: number) => {
    try {
      const historySnapshot = currentHistory.slice(0, targetIdx);
      const modelName = type === 'flash' ? 'gemini-3-flash-preview' : 'gemini-3.1-pro-preview';
      
      const stream = await sendChatMessageStream(historySnapshot, msg, modelName);
      let fullText = "";
      let grounding: any = null;
      
      for await (const chunk of stream) {
        fullText += (chunk.text || "");
        if (chunk.candidates?.[0]?.groundingMetadata) grounding = chunk.candidates[0].groundingMetadata;
        
        // Log usage if metadata is available in the chunk (usually the last one)
        if (chunk.usageMetadata) {
          logUsage(chunk, modelName);
        }

        setCurrentHistory(prev => {
          const next = [...prev];
          const targetMsg = next[targetIdx];
          if (targetMsg && targetMsg.role === 'model') {
             if (type === 'flash') { 
                targetMsg.parts = [{ text: fullText }]; 
                targetMsg.groundingMetadata = grounding; 
             } else { 
                targetMsg.proParts = [{ text: fullText }]; 
                targetMsg.proGroundingMetadata = grounding; 
             }
          }
          return next;
        });
      }
      
      const finalHistory = await new Promise<GeminiContent[]>(resolve => { 
        setCurrentHistory(prev => { resolve(prev); return prev; }); 
      });
      if (user) await recordROIEvent(user.uid, ROIEventType.REFINE);
      await updateChatHistory(user!.uid, sId, finalHistory);
    } catch (e: any) { 
      console.error(`Fallo Motor ${type.toUpperCase()}: ${e.message}`);
      setLoadingProMap(prev => ({ ...prev, [targetIdx]: false }));
    }
  };

  const handleCreateSummary = async (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    if (summarizingId || !user) return;
    setSummarizingId(session.id);
    try {
      const history = await getChatHistory(user.uid, session.id);
      if (history.length === 0) return;
      const content = await generateSessionSummary(history);
      if (content) {
        await saveChatSummary(user.uid, {
          id: `sum_${Date.now()}`,
          sessionId: session.id,
          userId: user.uid,
          title: `SÍNTESIS PhD: ${session.title}`,
          content: content,
          tags: ['PhD'],
          createdAt: Date.now()
        });
        setActiveTab('SUMMARIES');
      }
    } finally { setSummarizingId(null); }
  };

  const ReferenceBlock = ({ grounding }: { grounding?: any }) => {
    if (!grounding || !grounding.groundingChunks) return null;
    const sources = grounding.groundingChunks.map((c: any) => c.web).filter((w: any) => w && w.uri);
    if (sources.length === 0) return null;
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
        <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Library size={10}/> Bibliografía Médica</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {sources.map((src: any, idx: number) => (
            <a key={idx} href={src.uri} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-between p-1.5 rounded-xl border text-[10px] font-bold transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-blue-400 hover:bg-gray-800' : 'bg-blue-50 border-blue-100 text-blue-700 hover:bg-blue-100'}`}>
               <span className="truncate pr-2">{src.title || 'Referencia'}</span>
               <ExternalLink size={10} className="shrink-0 opacity-40" />
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full transition-colors duration-300 ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
      <div className={`px-6 py-4 border-b flex justify-between items-center shadow-sm shrink-0 z-30 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {activeSession ? (
            <button onClick={() => setActiveSession(null)} className={`p-2 rounded-full shrink-0 ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}><ArrowLeft size={22} /></button>
          ) : (
            <div className="bg-blue-600 p-1.5 rounded-lg shadow-lg shrink-0"><MessageSquare size={18} className="text-white" /></div>
          )}
          <h1 className={`font-black text-lg tracking-tighter uppercase font-['Outfit'] truncate ${isDarkMode ? 'text-gray-100' : 'text-slate-900'}`}>{activeSession ? activeSession.title : 'Búsqueda IA'}</h1>
        </div>
        <div className="flex items-center space-x-2">
           <button onClick={toggleDarkMode} className={`p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-yellow-400' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}>
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
           </button>
           {onNavigate && (
             <button 
               onClick={() => onNavigate(ViewState.REPORTS)}
               className={`p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-red-500'}`}
               title="Cerrar"
             >
               <Trash2 size={18} />
             </button>
           )}
        </div>
      </div>

      {!activeSession && (
        <div className={`flex border-b ${isDarkMode ? 'bg-[#2a2d34]/50 border-gray-700' : 'bg-white border-gray-100'}`}>
           <button onClick={() => setActiveTab('CHAT')} className={`flex-1 py-3 text-[10px] font-black tracking-widest border-b-2 transition-all ${activeTab === 'CHAT' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400'}`}>HISTORIAL</button>
           <button onClick={() => setActiveTab('SUMMARIES')} className={`flex-1 py-3 text-[10px] font-black tracking-widest border-b-2 transition-all ${activeTab === 'SUMMARIES' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>RESÚMENES PhD</button>
        </div>
      )}

      {activeTab === 'SUMMARIES' && !activeSession && (
        <div className={`px-6 py-3 border-b ${isDarkMode ? 'bg-[#1e2024] border-gray-800' : 'bg-white border-gray-100'}`}>
           <div className="relative max-w-6xl mx-auto">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Buscar en síntesis académicas..." 
                className={`w-full pl-11 pr-4 py-2.5 border rounded-2xl text-[11px] font-bold outline-none transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-gray-100 focus:ring-1 focus:ring-indigo-900' : 'bg-gray-50 border-gray-100 text-gray-900 focus:ring-1 focus:ring-indigo-100'}`}
                value={summarySearch}
                onChange={e => setSummarySearch(e.target.value)}
              />
           </div>
        </div>
      )}

      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar scroll-smooth">
        {!activeSession ? (
          activeTab === 'CHAT' ? (
            <div className="space-y-3 max-w-6xl mx-auto">
              {sessions.map(s => (
                <div key={s.id} onClick={() => setActiveSession(s)} className={`p-5 rounded-[28px] border shadow-sm cursor-pointer hover:shadow-md transition-all group ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 hover:bg-gray-800' : 'bg-white border-slate-100 hover:bg-slate-50'}`}>
                  <div className="flex justify-between items-center">
                     <div className="min-w-0 flex-1 pr-4">
                        <h3 className={`font-black text-[13px] uppercase truncate ${isDarkMode ? 'text-gray-100' : 'text-slate-800'}`}>{s.title}</h3>
                        <p className="text-[9px] text-gray-400 font-bold uppercase mt-1 flex items-center gap-1.5"><History size={10}/> {new Date(s.updatedAt).toLocaleDateString()}</p>
                     </div>
                     <div className="flex items-center space-x-1">
                        <button onClick={(e) => handleCreateSummary(e, s)} className={`p-2 rounded-lg transition-all ${summarizingId === s.id ? 'text-indigo-500 animate-spin' : 'text-gray-300 hover:text-indigo-600'}`} title="Resumen PhD">{summarizingId === s.id ? <RefreshCw size={16}/> : <BookOpen size={16}/>}</button>
                        <button onClick={(e) => { e.stopPropagation(); deleteChatSession(user!.uid, s.id); }} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={16}/></button>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4 max-w-6xl mx-auto pb-10">
              {filteredSummaries.length === 0 && (
                <div className="flex flex-col items-center py-20 opacity-30">
                   <FileSearch size={48} className="text-gray-400 mb-4" />
                   <p className="text-[10px] font-black uppercase tracking-widest">No se encontraron resúmenes</p>
                </div>
              )}
              {filteredSummaries.map(sum => {
                const isExpanded = expandedSummaries.has(sum.id);
                return (
                  <div key={sum.id} className={`rounded-[32px] border shadow-sm transition-all overflow-hidden ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-slate-100 shadow-slate-200/50'}`}>
                     <div 
                        onClick={() => toggleSummaryExpansion(sum.id)}
                        className={`p-6 flex justify-between items-center cursor-pointer transition-colors ${isExpanded ? (isDarkMode ? 'bg-indigo-900/10' : 'bg-indigo-50/30') : ''}`}
                     >
                        <div className="flex items-center gap-3">
                           <div className="bg-indigo-600 p-2 rounded-xl text-white shadow-lg shadow-indigo-600/20"><Sparkles size={16}/></div>
                           <div className="min-w-0">
                              <h4 className={`text-[12px] font-black uppercase truncate tracking-tight ${isDarkMode ? 'text-white' : 'text-indigo-900'}`}>{sum.title}</h4>
                              <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-1">Generado el {new Date(sum.createdAt).toLocaleDateString()}</p>
                           </div>
                        </div>
                        <div className="flex items-center space-x-3">
                           <button onClick={(e) => { e.stopPropagation(); deleteChatSummary(user!.uid, sum.id); }} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={16}/></button>
                           <div className="text-indigo-500">{isExpanded ? <ChevronUp size={20}/> : <ChevronDown size={20}/>}</div>
                        </div>
                     </div>
                     {isExpanded && (
                        <div className="p-8 pt-0 animate-in slide-in-from-top-2 duration-300">
                           <div className="h-px bg-gray-100 dark:bg-gray-800 mb-5" />
                           <div className={`text-[14px] font-medium chat-prose leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-slate-700'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(parseIAContent(sum.content)) }} />
                        </div>
                     )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="max-w-6xl mx-auto space-y-6">
            {currentHistory.map((msg, i) => {
               const isUser = msg.role === 'user';
               const hasPro = !!msg.proParts?.[0]?.text;
               const hasFlash = !!msg.parts?.[0]?.text;
               const isViewingPro = viewingProMap[i] || false;
               const isProLoading = loadingProMap[i] || false;

               const content = isViewingPro ? (msg.proParts?.[0]?.text || "") : (msg.parts?.[0]?.text || "");
               const grounding = isViewingPro ? msg.proGroundingMetadata : msg.groundingMetadata;
               
               return (
                  <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[95%] sm:max-w-[85%] rounded-[28px] px-6 py-5 text-[15px] shadow-sm border ${isUser ? 'bg-blue-600 text-white border-blue-500' : (isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-gray-100 shadow-xl' : 'bg-white border-gray-200 text-slate-800 shadow-md')}`}>
                      
                      {!isUser && (
                         <div className="flex flex-wrap items-center gap-2 mb-3">
                            <div className={`flex rounded-xl p-0.5 border ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                               <button 
                                 onClick={() => setViewingProMap({...viewingProMap, [i]: false})} 
                                 className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase transition-all ${!isViewingPro ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400'}`}
                               >
                                 <Zap size={10} className="inline mr-1"/> Motor Flash {hasFlash ? '✓' : ''}
                               </button>
                               <button 
                                 onClick={() => {
                                   if (!hasPro) handleRequestProAnalysis(i);
                                   else setViewingProMap({...viewingProMap, [i]: true});
                                 }} 
                                 className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase transition-all ${isViewingPro ? 'bg-[#10b981] text-white shadow-sm' : (hasPro ? 'text-gray-400' : 'text-indigo-500 hover:bg-indigo-50/10')} ${isProLoading ? 'animate-pulse opacity-70' : ''}`}
                               >
                                 {isProLoading ? <RefreshCw size={10} className="inline mr-1 animate-spin" /> : <GraduationCap size={10} className="inline mr-1"/>}
                                 Motor PhD {hasPro ? '✓' : ''}
                               </button>
                            </div>
                         </div>
                      )}

                      <div className="font-medium chat-prose">
                         {isProLoading && isViewingPro && !content ? (
                            <div className="flex flex-col items-center py-4 space-y-3 opacity-60">
                               <RefreshCw size={24} className="animate-spin text-emerald-500" />
                               <span className="text-[10px] font-black uppercase tracking-widest italic animate-pulse">Generando razonamiento PhD...</span>
                            </div>
                         ) : content ? ( 
                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(parseIAContent(content)) }} /> 
                         ) : ( 
                            !isUser && ( 
                               <div className="flex flex-col items-center py-4 space-y-3 opacity-60">
                                  <RefreshCw size={24} className="animate-spin text-blue-500" />
                                  <span className="text-[10px] font-black uppercase tracking-widest italic animate-pulse">Generando respuesta Flash...</span>
                               </div> 
                            ) 
                         )}
                      </div>
                      
                      {!isUser && content && <ReferenceBlock grounding={grounding} />}
                    </div>
                  </div>
               );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className={`p-6 border-t pb-24 md:pb-10 relative ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-200'}`}>
         <div className={`flex items-end space-x-3 max-w-6xl mx-auto rounded-[32px] p-3 border shadow-xl ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
            <textarea 
              value={inputMessage} 
              onChange={e => setInputMessage(e.target.value)} 
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())} 
              placeholder="Haz una consulta clínica técnica..." 
              className={`flex-1 bg-transparent border-none outline-none text-[15px] p-3 max-h-48 min-h-[56px] resize-none font-medium ${isDarkMode ? 'text-white placeholder-gray-600' : 'text-black placeholder-gray-500'}`} 
              rows={1} 
            />
            <button 
              onClick={handleSendMessage} 
              disabled={isSending || !inputMessage.trim()} 
              className={`p-4 rounded-[20px] transition-all shrink-0 ${(!inputMessage.trim() || isSending) ? 'text-gray-300' : 'bg-blue-600 text-white shadow-2xl active:scale-90 hover:bg-blue-700'}`}
            >
              {isSending ? <RefreshCw size={24} className="animate-spin" /> : <Send size={24} />}
            </button>
         </div>
      </div>
      <style>{` 
        .chat-prose b { font-weight: 800; color: inherit; } 
        .chat-prose { line-height: 1.55; }
      `}</style>
    </div>
  );
};

export default ChatScreen;
